import React, { Fragment, useCallback, useMemo } from 'react';
import { map } from 'lodash';
import PropTypes from 'prop-types';
import { isValid, format, parse } from 'date-fns';

import defaultUnitFilters from 'config/filters';

import { Button } from 'components/core/Button';
import { Link } from 'components/core/Link';
import { Datepicker } from 'components/core/Datepicker';
import Remove from 'components/svg/Remove';

import FilterGroup from './FilterGroup';
import './FiltersList.scss';

export function FiltersList({
	filters: filtersProp,
	onFilter,
	delayFiltering,
	searchParams,
	searchType,
	onShowModal,
	showSearchTypeLink,
	listKey,
	facetCounts,
	useNewSearch,
	unitFilters = defaultUnitFilters,
	storageTypeUrls = {},
}) {
	const onMoveInDateChange = useCallback(
		(event) => {
			onFilter({ moveInDate: event.target.value.string }, delayFiltering);
		},
		[onFilter, delayFiltering],
	);

	const onClearMoveInDate = useCallback(() => {
		onFilter({ moveInDate: null }, delayFiltering);
	}, [onFilter, delayFiltering]);

	const { moveInDate, filters, searchToggleText, searchToggleLink } =
		useMemo(() => {
			let toggleText = 'Self-Storage';
			if (searchType !== 'vehicle') {
				const isCarClp = /car-storage$/i.test(storageTypeUrls.vehicle);
				toggleText = isCarClp ? 'Car Storage' : 'Vehicle Storage';
			}
			return {
				moveInDate: (() => {
					const parsedDate = searchParams.moveInDate
						? parse(
								searchParams.moveInDate,
								'MM/dd/yyyy',
								new Date(),
							)
						: null;
					return parsedDate && isValid(parsedDate)
						? format(parsedDate, 'MMM d, yyyy')
						: '';
				})(),
				filters: filtersProp || unitFilters[searchType],
				searchToggleText: toggleText,
				searchToggleLink:
					searchType === 'vehicle'
						? storageTypeUrls.storage
						: storageTypeUrls.vehicle,
			};
		}, [
			searchParams,
			searchType,
			unitFilters,
			storageTypeUrls,
			filtersProp,
		]);

	return (
		<Fragment>
			{useNewSearch && (
				<div className="datepicker-filter">
					<Datepicker
						label="Planned Start Date:"
						name="moveInDate"
						placeholder="Select Date"
						withIcon
						value={moveInDate}
						onChange={onMoveInDateChange}
						id="search-filter-mid"
					/>
					{!!moveInDate && (
						<Button
							className="clear-field"
							onClick={onClearMoveInDate}
						>
							<Remove />
						</Button>
					)}
				</div>
			)}
			<div className="filter-lists">
				{map(filters, (filter, key) => (
					<FilterGroup
						facetCounts={facetCounts}
						listKey={listKey}
						key={key}
						filterName={key}
						allowMultiple={filter.multiple}
						label={filter.label}
						options={filter.options}
						searchParams={searchParams}
						onFilter={onFilter}
						delayFiltering={delayFiltering}
						filterType={filter.type}
						showing={filter.default}
						modal={filter.modal}
						onShowModal={onShowModal}
						useNewSearch={useNewSearch}
					/>
				))}
			</div>
			{showSearchTypeLink && (
				<Link
					href={searchToggleLink}
					className="search-toggle-link"
				>
					View {searchToggleText}
				</Link>
			)}
			{!useNewSearch && (
				<div className="datepicker-filter">
					<Datepicker
						label="Planned Start Date:"
						name="moveInDate"
						placeholder="Select Date"
						withIcon
						value={moveInDate}
						onChange={onMoveInDateChange}
						id="search-filter-mid"
					/>
					{!!moveInDate && (
						<Button
							className="clear-field"
							onClick={onClearMoveInDate}
						>
							<Remove />
						</Button>
					)}
				</div>
			)}
		</Fragment>
	);
}

FiltersList.propTypes = {
	listKey: PropTypes.string,
	filters: PropTypes.object,
	onFilter: PropTypes.func.isRequired,
	delayFiltering: PropTypes.bool,
	onShowModal: PropTypes.func,
	moveInDate: PropTypes.string,
	searchParams: PropTypes.object.isRequired,
	searchType: PropTypes.string,
	showSearchTypeLink: PropTypes.bool,
	facetCounts: PropTypes.object,
	useNewSearch: PropTypes.bool,
	unitFilters: PropTypes.object,
	storageTypeUrls: PropTypes.object,
};

export default FiltersList;
